import { useEffect, useState } from "react";

import {
  faCancel,
  faCheck,
  faCreditCard,
  faCrown,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useLanguage } from "../../contexts/LanguageContext";
import { useToast } from "../../contexts/ToastContext";
import { useUser } from "../../contexts/UserContext";
import PayPalButton from "../PayPalButton/PayPalButton";

import "./PricingModal.css";

const PricingModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const { user, setUser } = useUser();
  const { showSuccess, showError } = useToast();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [showPayPal, setShowPayPal] = useState(false);
  const [price, setPrice] = useState(t("premiumFeatures.premium.priceMonthly"));

  // Estados para gestos móviles
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Detectar dispositivos móviles
  const isMobile = window.innerWidth <= 768;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevenir scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Prevenir zoom en iOS cuando se hace doble tap
      document.addEventListener("touchstart", preventZoom, { passive: false });
    } else {
      document.body.style.overflow = "unset";
      document.removeEventListener("touchstart", preventZoom);
    }

    return () => {
      document.body.style.overflow = "unset";
      document.removeEventListener("touchstart", preventZoom);
    };
  }, [isOpen]);

  const preventZoom = (e) => {
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  };

  // Manejar gestos de swipe para cerrar en móvil
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isDownSwipe = distance < -50; // Swipe hacia abajo de al menos 50px

    if (isDownSwipe && isMobile) {
      onClose();
    }
  };

  const handlePayPalSuccess = async (result) => {
    try {
      console.log("PayPal Success Handler called with result:", result);
      setIsUpgrading(true);

      if (!user) {
        console.log("No user found, redirecting to auth");
        window.location.href = "/auth";
        return;
      }

      // El pago ya fue procesado por PayPalButton, solo necesitamos actualizar el estado del usuario
      if (result?.success) {
        console.log("Payment successful, updating user...");

        // Update user state with the premium status from the result
        const updatedUser = {
          ...user,
          isPremium: true,
          eventLimit: null,
          premiumStartDate: result.premium_status?.premium_start_date,
          premiumEndDate: result.premium_status?.premium_end_date,
          subscriptionType: result.premium_status?.subscription_type,
        };

        // Update user context and localStorage
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));

        showSuccess(t("paypalButton.success"));

        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(t("paypalButton.errorPaymentProcessing"));
      }
    } catch (error) {
      showError(t("paypalButton.errorPaymentProcessing"));
    } finally {
      setIsUpgrading(false);
      setShowPayPal(false);
    }
  };

  const handlePayPalError = (error) => {
    setShowPayPal(false);
    setIsUpgrading(false);
  };

  const handleUpgrade = async (plan) => {
    if (!user) {
      window.location.href = "/auth";
      return;
    }

    // Mostrar opciones de pago
    setShowPayPal(true);
  };

  if (!isOpen) return null;

  const switchPlan = (plan) => {
    setSelectedPlan(plan);
    if (plan === "annual") {
      setPrice(t("premiumFeatures.premium.priceAnnual"));
    } else if (plan === "daily") {
      setPrice(t("premiumFeatures.eventPremium.priceDaily"));
    } else {
      setPrice(t("premiumFeatures.premium.priceMonthly"));
    }
  };

  return (
    <div
      className="pricing-modal-overlay"
      onClick={handleOverlayClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div onClick={(e) => e.stopPropagation()}>
        {isMobile && (
          <div className="mobile-swipe-indicator">
            <div className="swipe-bar"></div>
          </div>
        )}
        <div className="pricing-modal">
          <button className="pricing-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className="pricing-header">
            <h2>{t("premiumFeatures.subtitle")}</h2>
            <div className="pricing-toggle">
              <span
                className={`pricing-toggle-label ${
                  selectedPlan === "daily" ? "active" : ""
                }`}
                onClick={() => switchPlan("daily")}
              >
                {t("premiumFeatures.daily")}
              </span>
              <span
                className={`pricing-toggle-label ${
                  selectedPlan === "monthly" ? "active" : ""
                }`}
                onClick={() => switchPlan("monthly")}
              >
                {t("premiumFeatures.monthly")}
              </span>
              <span
                className={`pricing-toggle-label ${
                  selectedPlan === "annual" ? "active" : ""
                }`}
                onClick={() => switchPlan("annual")}
              >
                {t("premiumFeatures.anual")}
              </span>
            </div>
            <p className="pricing-discount">{t("premiumFeatures.save")}</p>
          </div>

          <div className="pricing-plans">
            {/* Free Plan */}
            <div className="pricing-plan pricing-plan-free">
              <h3>{t("premiumFeatures.free.title")}</h3>
              <div className="pricing-price">
                <span className="price">{t("premiumFeatures.free.price")}</span>
                <span className="period">
                  {t("premiumFeatures.free.monthly")}
                </span>
              </div>
              <button className="pricing-btn pricing-btn-outline" disabled>
                {t("premiumFeatures.free.current")}
              </button>
              <ul className="pricing-features">
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.free.features.0")}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.free.features.1")}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.free.features.2")}
                </li>
                <li>
                  <FontAwesomeIcon
                    icon={faCancel}
                    style={{ color: "#ff4757" }}
                  />{" "}
                  {t("premiumFeatures.free.features.5")}
                </li>
                <li>
                  <FontAwesomeIcon
                    icon={faCancel}
                    style={{ color: "#ff4757" }}
                  />{" "}
                  {t("premiumFeatures.free.features.3")}
                </li>
                <li>
                  <FontAwesomeIcon
                    icon={faCancel}
                    style={{ color: "#ff4757" }}
                  />{" "}
                  {t("premiumFeatures.free.features.4")}
                </li>
              </ul>
            </div>

            {/* Pro Plan */}
            <div className="pricing-plan pricing-plan-featured">
              <div className="pricing-badge">
                <FontAwesomeIcon icon={faCrown} />{" "}
                {t("premiumFeatures.premium.title")}
              </div>
              <h3>{t("premiumFeatures.premium.title")}</h3>
              <div className="pricing-price">
                <span className="price">{price}</span>
                <span className="period">
                  {selectedPlan === "monthly"
                    ? t("premiumFeatures.premium.monthly")
                    : selectedPlan === "annual"
                    ? t("premiumFeatures.premium.annual")
                    : t("premiumFeatures.eventPremium.daily")}
                </span>
              </div>

              {!showPayPal ? (
                <button
                  className="pricing-btn pricing-btn-primary"
                  onClick={handleUpgrade}
                  disabled={isUpgrading}
                >
                  <FontAwesomeIcon
                    icon={faCreditCard}
                    style={{ marginRight: "8px" }}
                  />
                  {isUpgrading
                    ? t("premiumFeatures.upgrading")
                    : t("premiumFeatures.upgradeButton")}
                </button>
              ) : (
                <div className="payment-section">
                  <h4 style={{ marginBottom: "15px", color: "#333" }}>
                    💳 Finalizar pago con PayPal
                  </h4>
                  {user && (
                    <PayPalButton
                      plan={selectedPlan}
                      onSuccess={handlePayPalSuccess}
                      onError={handlePayPalError}
                    />
                  )}
                  <button
                    className="pricing-btn pricing-btn-outline"
                    onClick={() => setShowPayPal(false)}
                    style={{ marginTop: "10px" }}
                  >
                    ← Volver
                  </button>
                </div>
              )}
              <ul className="pricing-features">
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.premium.features.0")}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.premium.features.1")}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.premium.features.5")}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.premium.features.2")}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.premium.features.3")}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} />{" "}
                  {t("premiumFeatures.premium.features.4")}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
